import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import PatientRecordForm from "@components/Booking/PatientRecordForm";
import useToggle from "@base/hooks/useToggle";
import { foldVietnamese } from "@base/modules/doctor/doctorProfile";
import { FamilyMode } from "@base/modules/patientSpecialPackage/packageDetailRules";
import { IPatientRecord, IPatientRecordData } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";

interface IProps {
  open: boolean;
  toggle: () => void;
  mode: FamilyMode;
  /** The record the package belongs to - the "for myself" option, and the one left out of the list. */
  ownerRecord?: IPatientRecord;
  onPicked: (record?: IPatientRecord) => void;
}

/**
 * Who the benefit is used for (§9.2 step 1): `allow_family_record` 1 offers both, 2 goes straight to
 * the relatives. The owner's own record is never in the relatives' list.
 */
export default function BeneficiaryPicker({ open, toggle, mode, ownerRecord, onPicked }: IProps) {
  //! state
  const { t } = useTranslation();
  const { open: openForm, shouldRender: shouldRenderForm, toggle: toggleForm } = useToggle();
  const [isChoosingRelative, setIsChoosingRelative] = useState<boolean>(mode === "FAMILY_ONLY");
  const [records, setRecords] = useState<IPatientRecordData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");

  //! function
  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await profileService.getByUserKey();

      setRecords(resp.data ?? []);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isChoosingRelative) {
      loadRecords();
    }
  }, [isChoosingRelative, loadRecords]);

  //! render
  const relatives = records.filter((item) => item.patient_record?.code !== ownerRecord?.code);
  const needle = foldVietnamese(keyword.trim());
  const visibleRecords = needle
    ? relatives.filter((item) =>
        [item.patient_record?.patient_name, item.patient_record?.patient_phone_number, item.patient_record?.contact_phone].some((value) =>
          foldVietnamese(String(value ?? "")).includes(needle),
        ),
      )
    : relatives;

  const renderOption = (title: string, description: string, onClick: () => void) => (
    <div onClick={onClick} className="p-3 rounded-lg border border-common-divider bg-common-white cursor-pointer hover:border-common-primary">
      <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-sm font-medium font-['Inter'] !mb-0">
        {title}
      </CommonStyles.TypographyUI>
      <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
        {description}
      </CommonStyles.TypographyUI>
    </div>
  );

  return (
    <>
      <CommonStyles.ModalConfirmationUI
        open={open}
        toggle={toggle}
        type="normal"
        hiddenAction
        title={isChoosingRelative ? t("packageBooking.chooseRecord") : t("packageBooking.chooseBeneficiary")}
        content={
          isChoosingRelative ? (
            <div className="flex flex-col gap-3">
              <CommonStyles.InputUI
                variant="filled"
                allowClear
                value={keyword}
                placeholder={t("booking.record.searchPlaceholder")}
                prefix={<CommonIcons.SearchOutlined className="text-common-subtext" />}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setKeyword(event.target.value)}
              />

              <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
                <button
                  type="button"
                  onClick={toggleForm}
                  className="p-3 rounded-lg border border-dashed border-common-primary bg-common-primary50 text-common-primary text-sm font-medium font-['Inter'] text-left"
                >
                  + {t("booking.record.addNew")}
                </button>

                {isLoading ? <CommonStyles.LoadingUI /> : null}

                {visibleRecords.map((item) => (
                  <div
                    key={item.patient_record?.code}
                    onClick={() => onPicked(item.patient_record)}
                    className="p-3 rounded-lg border border-common-divider bg-common-white cursor-pointer hover:border-common-primary"
                  >
                    <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-sm font-medium font-['Inter'] !mb-0">
                      {item.patient_record?.patient_name}
                    </CommonStyles.TypographyUI>
                    <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
                      {[
                        item.patient_record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
                        item.patient_record?.patient_dob ? dayjs(item.patient_record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
                        item.patient_record?.contact_phone || item.patient_record?.patient_phone_number,
                      ]
                        .filter(Boolean)
                        .join(" - ")}
                    </CommonStyles.TypographyUI>
                  </div>
                ))}

                {!isLoading && !visibleRecords.length ? (
                  <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
                    {t("booking.record.empty")}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {renderOption(t("packageBooking.forSelf"), t("packageBooking.forSelfDescription"), () => onPicked(ownerRecord))}
              {renderOption(t("packageBooking.forFamily"), t("packageBooking.forFamilyDescription"), () => setIsChoosingRelative(true))}
            </div>
          )
        }
      />

      {shouldRenderForm && (
        <PatientRecordForm
          open={openForm}
          toggle={toggleForm}
          existingNames={records.map((item) => String(item.patient_record?.patient_name ?? ""))}
          onCreated={(record) => {
            toggleForm();
            // A new relative is chosen straight away.
            onPicked(record);
          }}
        />
      )}
    </>
  );
}
